import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2 as Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  Alert,
  Tooltip,
  Rating,
  Avatar,
  CircularProgress,
  Snackbar,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import {
  Search,
  Star,
  StarBorder,
  Check,
  Close,
  Reply,
  Delete,
  Refresh,
  ThumbUp,
  ThumbDown,
  Verified,
  Store,
  ShoppingBag,
} from '@mui/icons-material';
import reviewsService, {
  ProductReview,
  StoreReview,
  ProductReviewStats,
  StoreReviewStats,
} from '../../services/reviews.service';
import shopService from '../../services/shop.service';
import { useTranslation } from 'react-i18next';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const Reviews: React.FC = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  // Stats
  const [productStats, setProductStats] = useState<ProductReviewStats | null>(null);
  const [storeStats, setStoreStats] = useState<StoreReviewStats | null>(null);

  // Product reviews
  const [productReviews, setProductReviews] = useState<ProductReview[]>([]);
  const [productPage, setProductPage] = useState(0);
  const [productRowsPerPage, setProductRowsPerPage] = useState(25);
  const [productTotal, setProductTotal] = useState(0);
  const [productSearch, setProductSearch] = useState('');
  const [productStatusFilter, setProductStatusFilter] = useState<'all' | 'approved' | 'pending'>('pending');
  const [productRatingFilter, setProductRatingFilter] = useState<number | 'all'>('all');
  const [selectedProductReviews, setSelectedProductReviews] = useState<string[]>([]);

  // Store reviews
  const [storeReviews, setStoreReviews] = useState<StoreReview[]>([]);
  const [storePage, setStorePage] = useState(0);
  const [storeRowsPerPage, setStoreRowsPerPage] = useState(25);
  const [storeTotal, setStoreTotal] = useState(0);
  const [storeStatusFilter, setStoreStatusFilter] = useState<'all' | 'approved' | 'pending'>('pending');
  const [storeRatingFilter, setStoreRatingFilter] = useState<number | 'all'>('all');
  const [selectedStoreReviews, setSelectedStoreReviews] = useState<string[]>([]);

  // Stores filter
  const [stores, setStores] = useState<{ id: string; name: string }[]>([]);
  const [storeFilter, setStoreFilter] = useState<string>('');

  // Reply dialog
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyType, setReplyType] = useState<'product' | 'store'>('product');
  const [replyReviewId, setReplyReviewId] = useState<string>('');
  const [replyText, setReplyText] = useState('');

  // Review detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<ProductReview | StoreReview | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (tabValue === 0) loadProductReviews();
    else loadStoreReviews();
  }, [tabValue, productPage, productRowsPerPage, productSearch, productStatusFilter, productRatingFilter, storeFilter]);

  useEffect(() => {
    if (tabValue === 1) loadStoreReviews();
  }, [storePage, storeRowsPerPage, storeStatusFilter, storeRatingFilter]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load stores
      const storesRes = await shopService.getStores({ limit: 100 });
      setStores(storesRes.data.map((s: any) => ({ id: s.id, name: s.name })));

      // Load stats
      await loadStats();

      // Load reviews
      await loadProductReviews();
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const [prodStatsRes, storeStatsRes] = await Promise.all([
        reviewsService.getProductReviewStats(storeFilter ? Number(storeFilter) : undefined),
        reviewsService.getStoreReviewStats(storeFilter ? Number(storeFilter) : undefined),
      ]);
      setProductStats(prodStatsRes.data);
      setStoreStats(storeStatsRes.data);
      if (prodStatsRes.needsSetup) setNeedsSetup(true);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadProductReviews = async () => {
    try {
      const res = await reviewsService.getProductReviews({
        page: productPage + 1,
        limit: productRowsPerPage,
        storeId: storeFilter ? Number(storeFilter) : undefined,
        status: productStatusFilter !== 'all' ? productStatusFilter : undefined,
        rating: productRatingFilter !== 'all' ? productRatingFilter : undefined,
        search: productSearch || undefined,
      });
      setProductReviews(res.data);
      setProductTotal(res.pagination.total);
      if (res.needsSetup) setNeedsSetup(true);
    } catch (error) {
      console.error('Error loading product reviews:', error);
    }
  };

  const loadStoreReviews = async () => {
    try {
      const res = await reviewsService.getStoreReviews({
        page: storePage + 1,
        limit: storeRowsPerPage,
        storeId: storeFilter ? Number(storeFilter) : undefined,
        status: storeStatusFilter !== 'all' ? storeStatusFilter : undefined,
        rating: storeRatingFilter !== 'all' ? storeRatingFilter : undefined,
      });
      setStoreReviews(res.data);
      setStoreTotal(res.pagination.total);
    } catch (error) {
      console.error('Error loading store reviews:', error);
    }
  };

  const handleSetup = async () => {
    try {
      await reviewsService.setupTables();
      setNeedsSetup(false);
      setSnackbar({ open: true, message: t('shop.reviewsSetupSuccess'), severity: 'success' });
      loadInitialData();
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.errorSetupReviews'), severity: 'error' });
    }
  };

  const handleApproveProductReview = async (id: string) => {
    try {
      await reviewsService.approveProductReview(Number(id));
      setSnackbar({ open: true, message: t('shop.reviewApproved'), severity: 'success' });
      loadProductReviews();
      loadStats();
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.reviewApproveError'), severity: 'error' });
    }
  };

  const handleRejectProductReview = async (id: string) => {
    try {
      await reviewsService.rejectProductReview(Number(id));
      setSnackbar({ open: true, message: t('shop.reviewRejected'), severity: 'success' });
      loadProductReviews();
      loadStats();
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.reviewRejectError'), severity: 'error' });
    }
  };

  const handleApproveStoreReview = async (id: string) => {
    try {
      await reviewsService.approveStoreReview(Number(id));
      setSnackbar({ open: true, message: t('shop.reviewApproved'), severity: 'success' });
      loadStoreReviews();
      loadStats();
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.reviewApproveError'), severity: 'error' });
    }
  };

  const handleDeleteReview = async (id: string, type: 'product' | 'store') => {
    if (!window.confirm(t('shop.confirmDeleteReview'))) return;

    try {
      if (type === 'product') {
        await reviewsService.deleteProductReview(Number(id));
        loadProductReviews();
      } else {
        await reviewsService.deleteStoreReview(Number(id));
        loadStoreReviews();
      }
      setSnackbar({ open: true, message: t('shop.reviewDeleted'), severity: 'success' });
      loadStats();
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.reviewDeleteError'), severity: 'error' });
    }
  };

  const handleOpenReplyDialog = (reviewId: string, type: 'product' | 'store', existingReply?: string) => {
    setReplyReviewId(reviewId);
    setReplyType(type);
    setReplyText(existingReply || '');
    setReplyDialogOpen(true);
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim()) return;

    try {
      if (replyType === 'product') {
        await reviewsService.replyProductReview(Number(replyReviewId), replyText);
        loadProductReviews();
      } else {
        await reviewsService.replyStoreReview(Number(replyReviewId), replyText);
        loadStoreReviews();
      }
      setReplyDialogOpen(false);
      setSnackbar({ open: true, message: t('shop.replySent'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.replyError'), severity: 'error' });
    }
  };

  const handleBulkApprove = async (type: 'product' | 'store') => {
    const ids = type === 'product' ? selectedProductReviews : selectedStoreReviews;
    if (ids.length === 0) return;

    try {
      if (type === 'product') {
        await reviewsService.bulkApproveProductReviews(ids.map(Number));
        setSelectedProductReviews([]);
        loadProductReviews();
      } else {
        await reviewsService.bulkApproveStoreReviews(ids.map(Number));
        setSelectedStoreReviews([]);
        loadStoreReviews();
      }
      setSnackbar({ open: true, message: t('shop.bulkApproveSuccess', { count: ids.length }), severity: 'success' });
      loadStats();
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.bulkApproveError'), severity: 'error' });
    }
  };

  const handleSelectAllProductReviews = (checked: boolean) => {
    if (checked) {
      setSelectedProductReviews(productReviews.filter(r => !r.approved).map(r => r.id));
    } else {
      setSelectedProductReviews([]);
    }
  };

  const handleSelectAllStoreReviews = (checked: boolean) => {
    if (checked) {
      setSelectedStoreReviews(storeReviews.filter(r => !r.approved).map(r => r.id));
    } else {
      setSelectedStoreReviews([]);
    }
  };

  const RatingDistribution = ({ distribution }: { distribution: ProductReviewStats['distribution'] }) => {
    const total = Object.values(distribution).reduce((a, b) => a + b, 0);
    return (
      <Box>
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = distribution[rating as keyof typeof distribution];
          const percentage = total > 0 ? (count / total) * 100 : 0;
          return (
            <Box key={rating} display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="body2" sx={{ width: 20 }}>{rating}</Typography>
              <Star fontSize="small" sx={{ color: 'warning.main' }} />
              <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{ flex: 1, height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" sx={{ width: 30 }}>{count}</Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('shop.reviewsTitle')}</Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('shop.storeLabel')}</InputLabel>
            <Select
              value={storeFilter}
              label={t('shop.storeLabel')}
              onChange={(e) => setStoreFilter(e.target.value)}
            >
              <MenuItem value="">{t('shop.allStoresOption')}</MenuItem>
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button startIcon={<Refresh />} onClick={loadInitialData}>
            {t('shop.update')}
          </Button>
        </Box>
      </Box>

      {needsSetup && (
        <Alert severity="info" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={handleSetup}>
            {t('shop.setupReviews')}
          </Button>
        }>
          {t('shop.setupReviewsMsg')}
        </Alert>
      )}

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    {t('shop.productReviews')}
                  </Typography>
                  <Typography variant="h4">{productStats?.total || 0}</Typography>
                </Box>
                <ShoppingBag sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
              <Box display="flex" gap={2}>
                <Chip label={`${productStats?.pending || 0} ${t('shop.pendingStat')}`} color="warning" size="small" />
                <Chip label={`${productStats?.approved || 0} ${t('shop.approvedStat')}`} color="success" size="small" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    {t('shop.storeReviews')}
                  </Typography>
                  <Typography variant="h4">{storeStats?.total || 0}</Typography>
                </Box>
                <Store sx={{ fontSize: 40, color: 'secondary.main', opacity: 0.3 }} />
              </Box>
              <Box display="flex" gap={2}>
                <Chip label={`${storeStats?.pending || 0} ${t('shop.pendingStat')}`} color="warning" size="small" />
                <Chip label={`${storeStats?.approved || 0} ${t('shop.approvedStat')}`} color="success" size="small" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    {t('shop.overallAverage')}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h4">{productStats?.avgRating?.toFixed(1) || '0.0'}</Typography>
                    <Rating value={productStats?.avgRating || 0} precision={0.1} readOnly size="small" />
                  </Box>
                </Box>
                <Star sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
              </Box>
              {productStats && <RatingDistribution distribution={productStats.distribution} />}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab
            label={`${t('shop.productsTab')} (${productStats?.pending || 0} ${t('shop.pendingStat').toLowerCase()})`}
            icon={<ShoppingBag />}
            iconPosition="start"
          />
          <Tab
            label={`${t('shop.storesHeader')} (${storeStats?.pending || 0} ${t('shop.pendingStat').toLowerCase()})`}
            icon={<Store />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Product Reviews Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper>
          <Box p={2} display="flex" gap={2} flexWrap="wrap" justifyContent="space-between">
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                size="small"
                placeholder={t('shop.searchReviews')}
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>{t('shop.statusFilterLabel')}</InputLabel>
                <Select
                  value={productStatusFilter}
                  label={t('shop.statusFilterLabel')}
                  onChange={(e) => setProductStatusFilter(e.target.value as any)}
                >
                  <MenuItem value="all">{t('shop.allFilter')}</MenuItem>
                  <MenuItem value="pending">{t('shop.alertStatusPending')}</MenuItem>
                  <MenuItem value="approved">{t('shop.approvedStat')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>{t('shop.ratingFilter')}</InputLabel>
                <Select
                  value={productRatingFilter}
                  label={t('shop.ratingFilter')}
                  onChange={(e) => setProductRatingFilter(e.target.value as any)}
                >
                  <MenuItem value="all">{t('shop.allRatings')}</MenuItem>
                  {[5, 4, 3, 2, 1].map((r) => (
                    <MenuItem key={r} value={r}>{t('shop.starsLabel', { count: r })}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {selectedProductReviews.length > 0 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<Check />}
                onClick={() => handleBulkApprove('product')}
              >
                {t('shop.approveSelected', { count: selectedProductReviews.length })}
              </Button>
            )}
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedProductReviews.length === productReviews.filter(r => !r.approved).length && productReviews.filter(r => !r.approved).length > 0}
                      indeterminate={selectedProductReviews.length > 0 && selectedProductReviews.length < productReviews.filter(r => !r.approved).length}
                      onChange={(e) => handleSelectAllProductReviews(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{t('shop.productHeader')}</TableCell>
                  <TableCell>{t('shop.clientReviewHeader')}</TableCell>
                  <TableCell align="center">{t('shop.ratingHeader')}</TableCell>
                  <TableCell>{t('shop.commentHeader')}</TableCell>
                  <TableCell align="center">{t('shop.reviewStatusHeader')}</TableCell>
                  <TableCell>{t('shop.reviewDateHeader')}</TableCell>
                  <TableCell align="center">{t('shop.reviewActionsHeader')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productReviews.map((review) => (
                  <TableRow key={review.id} sx={{ bgcolor: !review.approved ? 'warning.lighter' : undefined }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedProductReviews.includes(review.id)}
                        disabled={review.approved}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProductReviews([...selectedProductReviews, review.id]);
                          } else {
                            setSelectedProductReviews(selectedProductReviews.filter(id => id !== review.id));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">{review.productName}</Typography>
                        <Typography variant="caption" color="textSecondary">{review.storeName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                          {review.clientName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{review.clientName}</Typography>
                          {review.verified && (
                            <Chip icon={<Verified />} label={t('shop.verified')} size="small" color="success" sx={{ height: 20 }} />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Rating value={review.rating} readOnly size="small" />
                    </TableCell>
                    <TableCell>
                      <Box maxWidth={300}>
                        {review.title && (
                          <Typography variant="body2" fontWeight="medium">{review.title}</Typography>
                        )}
                        <Typography variant="body2" color="textSecondary" noWrap>
                          {review.comment || '-'}
                        </Typography>
                        {review.storeReply && (
                          <Chip icon={<Reply />} label={t('shop.replied')} size="small" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={review.approved ? t('shop.approved') : t('shop.pending')}
                        color={review.approved ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{reviewsService.formatDate(review.date)}</TableCell>
                    <TableCell align="center">
                      {!review.approved && (
                        <>
                          <Tooltip title={t('shop.approveTooltip')}>
                            <IconButton size="small" color="success" onClick={() => handleApproveProductReview(review.id)}>
                              <Check />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('shop.rejectTooltip')}>
                            <IconButton size="small" color="error" onClick={() => handleRejectProductReview(review.id)}>
                              <Close />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={t('shop.replyTooltip')}>
                        <IconButton size="small" onClick={() => handleOpenReplyDialog(review.id, 'product', review.storeReply)}>
                          <Reply />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('shop.deleteTooltip')}>
                        <IconButton size="small" color="error" onClick={() => handleDeleteReview(review.id, 'product')}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {productReviews.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary" py={4}>
                        {t('shop.noReviewsFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={productTotal}
            page={productPage}
            onPageChange={(_, p) => setProductPage(p)}
            rowsPerPage={productRowsPerPage}
            onRowsPerPageChange={(e) => {
              setProductRowsPerPage(parseInt(e.target.value, 10));
              setProductPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t('shop.rowsPerPage')}
          />
        </Paper>
      </TabPanel>

      {/* Store Reviews Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper>
          <Box p={2} display="flex" gap={2} flexWrap="wrap" justifyContent="space-between">
            <Box display="flex" gap={2}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>{t('shop.statusFilterLabel')}</InputLabel>
                <Select
                  value={storeStatusFilter}
                  label={t('shop.statusFilterLabel')}
                  onChange={(e) => setStoreStatusFilter(e.target.value as any)}
                >
                  <MenuItem value="all">{t('shop.allFilter')}</MenuItem>
                  <MenuItem value="pending">{t('shop.alertStatusPending')}</MenuItem>
                  <MenuItem value="approved">{t('shop.approvedStat')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>{t('shop.ratingFilter')}</InputLabel>
                <Select
                  value={storeRatingFilter}
                  label={t('shop.ratingFilter')}
                  onChange={(e) => setStoreRatingFilter(e.target.value as any)}
                >
                  <MenuItem value="all">{t('shop.allRatings')}</MenuItem>
                  {[5, 4, 3, 2, 1].map((r) => (
                    <MenuItem key={r} value={r}>{t('shop.starsLabel', { count: r })}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {selectedStoreReviews.length > 0 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<Check />}
                onClick={() => handleBulkApprove('store')}
              >
                {t('shop.approveSelected', { count: selectedStoreReviews.length })}
              </Button>
            )}
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedStoreReviews.length === storeReviews.filter(r => !r.approved).length && storeReviews.filter(r => !r.approved).length > 0}
                      indeterminate={selectedStoreReviews.length > 0 && selectedStoreReviews.length < storeReviews.filter(r => !r.approved).length}
                      onChange={(e) => handleSelectAllStoreReviews(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{t('shop.storeReviewHeader')}</TableCell>
                  <TableCell>{t('shop.clientReviewHeader')}</TableCell>
                  <TableCell align="center">{t('shop.overallRating')}</TableCell>
                  <TableCell align="center">{t('shop.serviceRating')}</TableCell>
                  <TableCell align="center">{t('shop.deliveryRating')}</TableCell>
                  <TableCell align="center">{t('shop.qualityRating')}</TableCell>
                  <TableCell>{t('shop.commentHeader')}</TableCell>
                  <TableCell align="center">{t('shop.reviewStatusHeader')}</TableCell>
                  <TableCell align="center">{t('shop.reviewActionsHeader')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {storeReviews.map((review) => (
                  <TableRow key={review.id} sx={{ bgcolor: !review.approved ? 'warning.lighter' : undefined }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedStoreReviews.includes(review.id)}
                        disabled={review.approved}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStoreReviews([...selectedStoreReviews, review.id]);
                          } else {
                            setSelectedStoreReviews(selectedStoreReviews.filter(id => id !== review.id));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{review.storeName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                          {review.clientName.charAt(0)}
                        </Avatar>
                        <Typography variant="body2">{review.clientName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Rating value={review.rating} readOnly size="small" />
                    </TableCell>
                    <TableCell align="center">
                      {review.serviceRating ? <Rating value={review.serviceRating} readOnly size="small" /> : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {review.deliveryRating ? <Rating value={review.deliveryRating} readOnly size="small" /> : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {review.qualityRating ? <Rating value={review.qualityRating} readOnly size="small" /> : '-'}
                    </TableCell>
                    <TableCell>
                      <Box maxWidth={200}>
                        <Typography variant="body2" color="textSecondary" noWrap>
                          {review.comment || '-'}
                        </Typography>
                        {review.storeReply && (
                          <Chip icon={<Reply />} label={t('shop.replied')} size="small" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={review.approved ? t('shop.approved') : t('shop.pending')}
                        color={review.approved ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {!review.approved && (
                        <Tooltip title={t('shop.approveTooltip')}>
                          <IconButton size="small" color="success" onClick={() => handleApproveStoreReview(review.id)}>
                            <Check />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t('shop.replyTooltip')}>
                        <IconButton size="small" onClick={() => handleOpenReplyDialog(review.id, 'store', review.storeReply)}>
                          <Reply />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('shop.deleteTooltip')}>
                        <IconButton size="small" color="error" onClick={() => handleDeleteReview(review.id, 'store')}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {storeReviews.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography color="textSecondary" py={4}>
                        {t('shop.noReviewsFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={storeTotal}
            page={storePage}
            onPageChange={(_, p) => setStorePage(p)}
            rowsPerPage={storeRowsPerPage}
            onRowsPerPageChange={(e) => {
              setStoreRowsPerPage(parseInt(e.target.value, 10));
              setStorePage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t('shop.rowsPerPage')}
          />
        </Paper>
      </TabPanel>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('shop.replyDialogTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('shop.storeReplyLabel')}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSubmitReply} disabled={!replyText.trim()}>
            {t('shop.sendReply')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reviews;
